import { getToken } from '@cinch-labs/shared-util'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { datadogRum } from '@datadog/browser-rum'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosStatic,
} from 'axios'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { create } from 'zustand'
import {
  UserPreferences,
  UserProfile,
  UserProfilePreferenceUpdate,
  UserState,
  UserStoreConfig,
} from './types'
import { mapPreferenceUpdateToUpdatedPreferences } from './util/preferences'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zE: any
  }
}

let userStoreConfig: UserStoreConfig = {
  authKey: '',
  profileServiceUrl: '',
  auth0ServiceUrl: '',
  auth0DatabaseName: '',
  auth0ClientId: '',
  redirectUrlKey: '',
  cinchUrl: '',
}
let httpClient: AxiosInstance
let userManagerInstance: UserManager | undefined
let onSessionExpiredCallback: () => void

let lastZendeskTokenUsed: string | undefined

const initializeUserManager = () => {
  if (userManagerInstance || typeof window === 'undefined') {
    return userManagerInstance
  }

  const userManager = new UserManager({
    authority: userStoreConfig.auth0ServiceUrl,
    client_id: userStoreConfig.auth0ClientId,
    redirect_uri: `${window.location.origin}/auth-callback`,
    silent_redirect_uri: `${window.location.origin}/silent-renew`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: 'openid email idt prf dd offline_access',
    automaticSilentRenew: true,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({
      prefix: 'oxford-',
      store: window.localStorage,
    }),
    extraQueryParams: {
      audience: 'https://cinch.co.uk',
      nx: 'true',
    },
  })

  const storedUser = localStorage.getItem(userStoreConfig.authKey)
  if (storedUser) {
    userManager.storeUser(new User(JSON.parse(storedUser)))
  }

  userManager.events.addUserLoaded(async (user) => {
    await loginZendesk(user?.access_token)
  })

  userManager.events.addSilentRenewError(async (error: Error) => {
    datadogRum.addError(new Error(`AddSilentRenewError: ${error.message}`))
    userManager.clearStaleState()
    userManager.removeUser()
  })

  userManager.events.addAccessTokenExpired(async () => {
    datadogRum.addError(new Error('AddAccessTokenExpired'))
    window.location.assign('/login')
  })

  userManagerInstance = userManager
  return userManager
}

const loginZendesk = async (auth0AccessToken?: string) => {
  if (!auth0AccessToken || typeof window === 'undefined' || !window.zE) {
    return
  }
  if (lastZendeskTokenUsed === auth0AccessToken) {
    return
  }
  lastZendeskTokenUsed = auth0AccessToken

  const VEHICLE_ISSUE_TRIAGE_URL = readFromEnv(Env.VehicleIssueTriageUrl)
  const ZENDESK_TOKEN_ENDPOINT = `${VEHICLE_ISSUE_TRIAGE_URL}/zendesk-token`

  try {
    const response = await axios.post(
      ZENDESK_TOKEN_ENDPOINT,
      {},
      {
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`,
        },
      },
    )
    window.zE('messenger', 'loginUser', (callback: (token: string) => void) => {
      callback(response.data.token)
    })
  } catch (error) {
    datadogRum.addError(new Error('LoginZendeskFailed'))
    throw error
  }
}

export const initUserStore = (
  config?: Partial<UserStoreConfig>,
  axiosImpl: AxiosStatic = axios,
) => {
  userStoreConfig = {
    auth0ServiceUrl: readFromEnv(Env.Auth0ServiceUrl),
    auth0DatabaseName: readFromEnv(Env.Auth0DatabaseName),
    auth0ClientId: readFromEnv(Env.Auth0ClientId),
    profileServiceUrl: readFromEnv(Env.ProfileServiceUrl),
    redirectUrlKey: readFromEnv(Env.RedirectUrlKey),
    authKey: readFromEnv(Env.AuthKey),
    cinchUrl: readFromEnv(Env.cinchUrl),
    ...config,
  }

  const _httpClient = axiosImpl.create()
  let accessToken: string | undefined

  _httpClient.interceptors.request.use(async (request) => {
    if (userManagerInstance) {
      const user = await userManagerInstance.getUser()

      request.headers ||= {} as AxiosRequestHeaders
      request.headers.Authorization = `Bearer ${user?.access_token}`

      return request
    }

    const userManager = initializeUserManager()
    if (!userManager) {
      throw new Error('UserManager failed to initialize')
    }

    const user = await userManager.getUser()

    if (user?.access_token) {
      void loginZendesk(user.access_token).catch(() => {
        // handled in loginZendesk
      })
    }

    accessToken = user?.access_token

    request.headers ||= {} as AxiosRequestHeaders
    request.headers.Authorization = `Bearer ${accessToken}`

    return request
  })

  _httpClient.interceptors.response.use(undefined, (error: AxiosError) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      datadogRum.addError(new Error('SessionExpiredError'))
      onSessionExpiredCallback?.()
    }

    return Promise.reject(error)
  })

  httpClient = _httpClient
}

export type UserStore = UserState & {
  isLoggedIn: () => boolean
  fetchUser: () => Promise<void>
  logout: () => void
  login: () => void
  register: () => void
  updateProfile: (updatedFields: Partial<UserProfile>) => Promise<void>
  updateProfilePreferences: (
    preferenceUpdate: UserProfilePreferenceUpdate,
  ) => Promise<void>
  resetPassword: (email: string) => Promise<{ statusCode: number }>
  setStatus: (status: UserState['status']) => void
  signInRedirect: (
    onSuccess: (user: User) => void,
    onError: (error: Error) => void,
  ) => void
}

export const useUserStore = create<UserStore>((set, get) => {
  onSessionExpiredCallback = () => {
    set({ status: 'expired' })
  }

  return {
    isLoggedIn: () => get().status === 'valid',

    status: 'unknown',

    setStatus: (status) => set({ status }),

    fetchUser: async () => {
      const { authKey, profileServiceUrl } = userStoreConfig
      if (typeof window === 'undefined') {
        return
      }

      const token = getToken({ authKey })

      if (!token) {
        return set(() => ({ status: 'invalid' }))
      }

      set(() => ({ status: 'pending', token }))

      try {
        const profile = (
          await httpClient.get<UserProfile & UserPreferences>(
            `${profileServiceUrl}/v2/userprofile`,
          )
        ).data

        const {
          preferenceCall,
          preferenceEmail,
          preferenceSms,
          ...profileWithoutPreferences
        } = profile

        return set(() => ({
          status: 'valid',
          profile: profileWithoutPreferences,
          preferences: {
            preferenceCall,
            preferenceEmail,
            preferenceSms,
          },
          token,
        }))
      } catch {
        datadogRum.addError(new Error('FetchUserProfileActionFailed'))
      }
    },

    resetPassword: async (email: string) => {
      try {
        const postUrl = '/dbconnections/change_password'
        const { auth0ServiceUrl, auth0DatabaseName, auth0ClientId } =
          userStoreConfig
        const data = {
          email: email,
          connection: auth0DatabaseName,
          client_id: auth0ClientId,
        }

        const axiosInstance = axios.create({
          baseURL: auth0ServiceUrl,
          validateStatus: () => true,
        })
        const result = await axiosInstance.post(postUrl, data)

        return {
          statusCode: result.status,
        }
      } catch (error: unknown) {
        datadogRum.addError(new Error('ResetPasswordActionFailed'))
        throw error
      }
    },

    updateProfile: async (updatedFields) => {
      try {
        const { profileServiceUrl } = userStoreConfig

        set(() => ({ status: 'updating' }))
        const fullProfile = { ...get().profile, ...updatedFields }
        const updatedProfile = { ...updatedFields }
        await httpClient.put<void>(
          `${profileServiceUrl}/v2/userprofile`,
          updatedProfile,
        )
        set(() => ({ profile: fullProfile, status: 'valid' }))
      } catch {
        datadogRum.addError(new Error('UpdateProfileActionFailed'))
      }
    },

    updateProfilePreferences: async (preferenceUpdate) => {
      try {
        const { profileServiceUrl } = userStoreConfig

        set(() => ({ status: 'updating' }))

        await httpClient.put<void>(`${profileServiceUrl}/v2/userprofile`, {
          ...preferenceUpdate,
          context: 'Updated from Marketing Preferences page in profile',
        })

        const existingPreferences = get().preferences

        set(() => ({
          preferences: {
            ...existingPreferences,
            ...mapPreferenceUpdateToUpdatedPreferences(preferenceUpdate),
          },
          status: 'valid',
        }))
      } catch {
        datadogRum.addError(new Error('UpdateProfilePreferencesActionFailed'))
      }
    },

    logout: () => {
      const { redirectUrlKey } = userStoreConfig
      lastZendeskTokenUsed = undefined

      if (typeof window !== 'undefined' && window.zE) {
        window.zE('messenger', 'logoutUser')
      }

      localStorage.removeItem(redirectUrlKey)
      window.location.assign('/logout')
    },

    login: () => {
      datadogRum.addAction('login redirect')
      window.location.assign('/login')
    },

    register: () => {
      window.location.assign('/login')
    },

    signInRedirect: (
      onSuccess: (user: User) => void,
      onError: (error: Error) => void,
    ) => {
      const userManager = initializeUserManager()
      if (!userManager) {
        onError(new Error('UserManager failed to initialize'))
        return
      }

      userManager
        .signinRedirectCallback()
        .then((user) => onSuccess(user))
        .catch((error) => onError(error))
    },
  }
})
